import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  StyleSheet,
  TextInput,
  Animated,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { goBack, navigate } from '../../navigation/RootNavigation';
import { useDispatch, useSelector } from 'react-redux';
import { removeAuthToken, saveUserProfile } from '../../redux/action/authActions';
import { COLORS, LAYOUT, BORDER_RADIUS } from '../../constants/theme';
import ajax from '../../helpers/ajaxHelper';
import Urls from '../../services/Urls';

/* ── Initials helper ─────────────────────────────────── */
const getInitials = (user) => {
  if (!user) return 'U';
  const firstName = (user?.first_name || user?.firstName || (typeof user?.name === 'string' ? user.name.split(' ')[0] : '') || '').trim();
  const lastName = (user?.last_name || user?.lastName || (typeof user?.name === 'string' && user.name.split(' ').length > 1 ? user.name.split(' ').slice(1).join(' ') : '') || '').trim();

  if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase();
  if (firstName) return firstName[0].toUpperCase();
  if (user?.email) return user.email.trim()[0].toUpperCase();
  return 'U';
};

/* ── Custom Logout Modal (mobile) ────────────────────── */
const LogoutModal = ({ visible, onConfirm, onCancel }) => {
  if (!visible) return null;
  return (
    <View style={lm.portalRoot}>
      {/* Backdrop */}
      <TouchableOpacity
        style={StyleSheet.absoluteFillObject}
        activeOpacity={1}
        onPress={onCancel}
      />

      {/* Card */}
      <View style={lm.card}>
        {/* Icon */}
        <View style={lm.iconWrap}>
          <Ionicons name="log-out-outline" size={28} color="#ef4444" />
        </View>

        <Text style={lm.title}>Log out?</Text>
        <Text style={lm.subtitle}>
          You'll be signed out of your account.{'\n'}You can log back in anytime.
        </Text>

        {/* Actions */}
        <TouchableOpacity style={lm.logoutBtn} onPress={onConfirm} activeOpacity={0.85}>
          <Ionicons name="log-out-outline" size={16} color="#fff" style={{ marginRight: 6 }} />
          <Text style={lm.logoutBtnText}>Yes, Log Out</Text>
        </TouchableOpacity>

        <TouchableOpacity style={lm.cancelBtn} onPress={onCancel} activeOpacity={0.75}>
          <Text style={lm.cancelBtnText}>Stay Signed In</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const lm = StyleSheet.create({
  portalRoot: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 99999,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    width: '88%',
    maxWidth: 320,
    backgroundColor: '#fff',
    borderRadius: 20,
    padding: 24,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.18,
    shadowRadius: 24,
    elevation: 20,
    zIndex: 100000,
  },
  iconWrap: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#fee2e2',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
  },
  title: {
    fontSize: 20,
    fontWeight: '800',
    color: '#1a1a2e',
    marginBottom: 8,
    fontFamily: 'poppins',
  },
  subtitle: {
    fontSize: 13,
    color: '#888',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
    fontFamily: 'poppins',
  },
  logoutBtn: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ef4444',
    borderRadius: 30,
    paddingVertical: 13,
    marginBottom: 10,
  },
  logoutBtnText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '700',
    fontFamily: 'poppins',
  },
  cancelBtn: {
    width: '100%',
    alignItems: 'center',
    paddingVertical: 12,
    borderRadius: 30,
    borderWidth: 1.5,
    borderColor: '#e5e7eb',
  },
  cancelBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#555',
    fontFamily: 'poppins',
  },
});

/* ── Main Mobile StatusBar ───────────────────────────── */
const StatusBarMobile = ({ backbutton, backNavigation }) => {
  const userToken   = useSelector((state) => state?.AppReducer?.token);
  const userProfile = useSelector((state) => state?.Login?.userDetails);
  const dispatch    = useDispatch();

  const [logoutVisible, setLogoutVisible] = useState(false);

  useEffect(() => {
    if (userToken && (!userProfile || !userProfile.first_name)) {
      ajax
        .get(Urls.PROFILE)
        .then(({ data }) => {
          if (data?.status) {
            const details = data?.data?.userDetails || data?.payload;
            if (details) {
              dispatch(saveUserProfile(details));
            }
          }
        })
        .catch((error) => console.log('StatusBarMobile profile error', error));
    }
  }, [userToken, userProfile]);

  const initials = getInitials(userProfile);

  const handleLogout = () => {
    setLogoutVisible(false);
    dispatch(removeAuthToken());
    navigate('login');
  };

  return (
    <>
      <View style={styles.container}>
        {/* Left: back button OR logo */}
        <View style={styles.leftSection}>
          {backbutton ? (
            <TouchableOpacity
              style={styles.backButton}
              onPress={() => backNavigation ? navigate(backNavigation) : goBack()}
              activeOpacity={0.7}
            >
              <Ionicons name="chevron-back" size={22} color={COLORS.primary} />
              <Text style={styles.backText}>
                {backNavigation === 'CurrentOnboards' ? 'Back To Current Tenancies' : 'Back'}
              </Text>
            </TouchableOpacity>
          ) : (
            /* Logo */
            <TouchableOpacity
              onPress={() => userToken ? navigate('home') : navigate('Hello')}
              activeOpacity={0.8}
            >
              <Image
                source={require('./../../assets/images/Logo/HubitaWeb.png')}
                style={styles.logo}
                resizeMode="contain"
              />
            </TouchableOpacity>
          )}
        </View>

        {/* Right: avatar */}
        {userToken ? (
          <View style={styles.rightSection}>
            {/* Avatar — user initials, navigate to account settings */}
            <TouchableOpacity
              style={styles.avatarCircle}
              onPress={() => navigate('AccountEdit')}
              activeOpacity={0.8}
            >
              <Text style={styles.avatarText}>{initials}</Text>
            </TouchableOpacity>
          </View>
        ) : null}
      </View>

      {/* Custom logout modal */}
      <LogoutModal
        visible={logoutVisible}
        onConfirm={handleLogout}
        onCancel={() => setLogoutVisible(false)}
      />
    </>
  );
};

/* ── Styles ──────────────────────────────────────────── */
const styles = StyleSheet.create({
  container: {
    height: LAYOUT.mobileTopBarHeight,
    backgroundColor: COLORS.surface,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
    zIndex: 10,
  },
  leftSection: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  logo: {
    height: 22,
    width: 100,
  },

  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: 8,
  },
  backText: {
    color: COLORS.primary,
    fontSize: 13,
    fontWeight: '600',
    marginLeft: 2,
    maxWidth: 200,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.surfaceHover || '#f5f5f9',
    borderRadius: BORDER_RADIUS.lg || 10,
    borderWidth: 1,
    borderColor: COLORS.primary,
    paddingHorizontal: 10,
    paddingVertical: 6,
    maxWidth: 240,
    overflow: 'hidden',
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: COLORS.textPrimary,
    paddingVertical: 0,
  },
  rightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
  },
  bellBtn: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: COLORS.surfaceHover,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  bellBtnActive: {
    backgroundColor: '#f0e8ff',
    borderColor: COLORS.primary,
  },
  notifDot: {
    position: 'absolute',
    top: -1, right: -1,
    width: 7, height: 7,
    borderRadius: 3.5,
    backgroundColor: COLORS.danger,
    borderWidth: 1,
    borderColor: COLORS.surface,
  },
  avatarCircle: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: COLORS.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 0.5,
    fontFamily: 'poppins',
  },
});

export default StatusBarMobile;
